import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ErrorBanner } from '../../components/Async'
import { ConfirmDialog } from '../../components/ConfirmDialog'
import { Dialog } from '../../components/Dialog'
import {
  ApiError,
  dictionaries,
  type BoundPolicySummary,
  type ContentRule,
  type DictionaryDetail,
  type DictionaryPreview
} from '../../lib/api'

const SAMPLE_PROMPT =
  'Can you summarize this? The wire transfer instructions are: routing number 021000021, ' +
  'beneficiary account 1234567890. My api key is sk-live-abcdef.'

/**
 * The dictionary editor.
 *
 * The preview is the part that earns its place. Telling an operator that "confidential" is too broad
 * a term is an argument they can disagree with; showing them that it fires on the paragraph they just
 * pasted is not. It runs the agent's own matcher, so what it shows is what the fleet will do.
 */
export function DictionaryEditorPage() {
  const { dictionaryId } = useParams<{ dictionaryId: string }>()
  const [detail, setDetail] = useState<DictionaryDetail | null>(null)
  const [failure, setFailure] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const [ruleName, setRuleName] = useState('')
  const [terms, setTerms] = useState('')
  const [severity, setSeverity] = useState('high')

  /**
   * Which rule the dialog is open on, if any.
   *
   * `null` is closed, and an empty name is a new rule. Editing and adding are the same request -
   * posting a content rule whose name already exists replaces it - so they are the same form, and
   * the only difference is whether it opens with the fields filled in. Before this the screen had no
   * edit affordance at all: changing one term meant retyping the rule and every other term in it,
   * into a form that was permanently open under the table.
   */
  const [editing, setEditing] = useState<ContentRule | null>(null)

  const openRule = (rule?: ContentRule) => {
    setRuleName(rule?.name ?? '')
    setTerms(rule ? rule.matchAny.join('\n') : '')
    setSeverity(rule?.severity ?? 'high')
    setEditing(rule ?? { name: '', matchAny: [], severity: 'high' })
  }

  const [sample, setSample] = useState(SAMPLE_PROMPT)
  const [preview, setPreview] = useState<DictionaryPreview | null>(null)

  // Set when a retire is refused because policies still bind this dictionary. Holding the names is
  // what lets the second attempt be an informed decision rather than the same button pressed harder.
  const [blocking, setBlocking] = useState<BoundPolicySummary[] | null>(null)

  /**
   * Which confirmation is open.
   *
   * Retiring navigated away the moment the button was pressed, and removing a rule took a term list
   * out of the dictionary the fleet matches on with no question asked at all. Neither is recoverable
   * from this screen: a retired dictionary has left the list, and a removed rule has to be retyped
   * term by term. Both are one click from the Publish button they sit beside.
   */
  const [asking, setAsking] = useState<{ kind: 'retire'; force: boolean } | { kind: 'rule'; name: string } | null>(null)
  const navigate = useNavigate()

  const load = useCallback(async () => {
    if (!dictionaryId) return
    try {
      setDetail(await dictionaries.get(dictionaryId))
    } catch (error) {
      setFailure(error instanceof ApiError ? error.message : 'The dictionary could not be loaded.')
    }
  }, [dictionaryId])

  useEffect(() => {
    void load()
  }, [load])

  const run = useCallback(
    async (work: () => Promise<string>) => {
      setBusy(true)
      setFailure(null)
      setNotice(null)
      try {
        setNotice(await work())
        await load()
      } catch (error) {
        setFailure(error instanceof ApiError ? error.message : 'The request failed.')
      } finally {
        setBusy(false)
      }
    },
    [load]
  )

  const addRule = () =>
    run(async () => {
      if (!dictionaryId) throw new ApiError(400, 'No dictionary is open.')
      const parsed = terms
        .split('\n')
        .map((term) => term.trim())
        .filter((term) => term.length > 0)

      const result = await dictionaries.addContentRule(dictionaryId, {
        name: ruleName,
        terms: parsed,
        severity,
        // Carried through rather than reset. These are not on the form, and an edit that silently
        // turned off whole-word matching would change what the rule fires on without saying so.
        caseSensitive: editing?.caseSensitive,
        wholeWord: editing?.wholeWord
      })
      setRuleName('')
      setTerms('')
      setEditing(null)
      return result.replaced
        ? `Replaced '${ruleName}' in revision ${result.revision}.`
        : `Added '${ruleName}' to revision ${result.revision}.`
    })

  const removeRule = (name: string) =>
    run(async () => {
      if (!dictionaryId) throw new ApiError(400, 'No dictionary is open.')
      const result = await dictionaries.removeRule(dictionaryId, name)
      return `Removed '${name}' from revision ${result.revision}.`
    })

  const publish = () =>
    run(async () => {
      if (!dictionaryId || !detail) throw new ApiError(400, 'No dictionary is open.')
      const result = await dictionaries.publish(dictionaryId, detail.revision)
      return `Published revision ${result.revision} with ${result.ruleCount} rule(s). Republish the policies that bind it to reach the fleet.`
    })

  /**
   * Retires the dictionary.
   *
   * Refused the first time if a policy still binds it, with the policies named. That is not
   * bureaucracy: publishing a policy whose bound dictionary has gone is refused outright, and the
   * operator who finds that out is usually not the one who retired it, weeks later, with nothing
   * connecting the two.
   */
  const retire = (force: boolean) => {
    if (!dictionaryId) return
    setBusy(true)
    setFailure(null)
    setNotice(null)
    dictionaries
      .retire(dictionaryId, 'Retired from the console.', force)
      .then(() => navigate('/policies?tab=dictionaries'))
      .catch((error: unknown) => {
        if (error instanceof ApiError && error.boundPolicies.length > 0) setBlocking(error.boundPolicies)
        setFailure(error instanceof ApiError ? error.message : 'The dictionary could not be retired.')
      })
      .finally(() => setBusy(false))
  }

  const runPreview = () =>
    run(async () => {
      if (!dictionaryId) throw new ApiError(400, 'No dictionary is open.')
      const result = await dictionaries.preview(dictionaryId, sample)
      setPreview(result)
      return result.matchCount === 0
        ? 'No rule matched this sample.'
        : `${result.matchCount} match(es), highest severity ${result.highestSeverity}.`
    })

  if (!detail) {
    // No refusal banner here. It used to be rendered in this branch, which is only ever reached
    // before the dictionary has loaded - so the "Retire anyway" button it carries could not be
    // reached by the one person who needs it, whose retire was refused with the page fully loaded.
    return (
      <>
        <ErrorBanner message={failure} />
        <div className="empty">Loading…</div>
      </>
    )
  }

  const warnings = detail.validation.filter((message) => message.severity === 'warning')
  const errors = detail.validation.filter((message) => message.severity === 'error')

  const blockedNames = blocking?.map((policy) => `'${policy.name}'`).join(', ') ?? ''

  return (
    <>
      <div className="page-header">
        <div>
          <h1>{detail.name}</h1>
          <p>
            Revision {detail.revision} · <span className="pill">{detail.revisionStatus}</span> ·{' '}
            {detail.ruleCount} rule(s), {detail.termCount} term(s)
          </p>
        </div>
        <div className="toolbar">
        <button
          type="button"
          onClick={() => setAsking({ kind: 'retire', force: false })}
          disabled={busy}
          data-testid="retire-dictionary"
        >
          Retire
        </button>
        <button
          type="button"
          className="primary"
          onClick={publish}
          disabled={busy || detail.revisionStatus !== 'draft' || detail.ruleCount === 0 || errors.length > 0}
          data-testid="publish-dictionary"
        >
          Publish revision {detail.revision}
        </button>
        </div>
      </div>

      <ErrorBanner message={failure} />
      {notice && (
        <div className="banner success" role="status" data-testid="dictionary-notice">
          {notice}
        </div>
      )}

      {blocking && blocking.length > 0 && (
        <div className="banner warning" data-testid="retire-blocked">
          <p style={{ margin: 0 }}>
            Still bound by {blockedNames}. Publishing {blocking.length === 1 ? 'it' : 'them'} would be
            refused until the binding is removed.
          </p>
          <div className="toolbar" style={{ marginTop: '0.6rem' }}>
            <button
              type="button"
              onClick={() => setAsking({ kind: 'retire', force: true })}
              disabled={busy}
              data-testid="retire-anyway"
            >
              Retire anyway
            </button>
            <button type="button" onClick={() => setBlocking(null)} disabled={busy}>
              Leave it
            </button>
          </div>
        </div>
      )}

      <div className="editor-grid">
        <div className="card">
          <div className="card-body">
            <div className="toolbar">
              <h3 style={{ marginTop: 0, marginBottom: 0 }} className="grow">
                Rules
              </h3>
              <button type="button" className="primary" onClick={() => openRule()} disabled={busy} data-testid="new-rule">
                Add a rule
              </button>
            </div>

            <div className="table-scroll">
              <table data-testid="rule-table">
                <thead>
                  <tr>
                    <th>Rule</th>
                    <th>Severity</th>
                    <th>Terms</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {detail.contentRules.map((rule) => (
                    <tr key={rule.name}>
                      <td>{rule.name}</td>
                      <td>
                        <span className={`pill sev-${severityRank(rule.severity)}`}>{rule.severity}</span>
                      </td>
                      <td className="muted">{rule.matchAny.join(', ')}</td>
                      <td>
                        <button
                          type="button"
                          onClick={() => openRule(rule)}
                          disabled={busy}
                          data-testid={`edit-rule-${rule.name}`}
                        >
                          Edit
                        </button>{' '}
                        <button
                          type="button"
                          onClick={() => setAsking({ kind: 'rule', name: rule.name })}
                          disabled={busy}
                          data-testid={`remove-rule-${rule.name}`}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                  {/* No Edit on a signal rule: the console has no endpoint that writes one, and a
                      button that could only ever delete and retype it is worse than its absence. */}
                  {detail.signalRules.map((rule) => (
                    <tr key={rule.name}>
                      <td>{rule.name}</td>
                      <td>
                        <span className="pill">signal</span>
                      </td>
                      <td className="muted">{rule.matchAny.join(', ')}</td>
                      <td>
                        <button
                          type="button"
                          onClick={() => setAsking({ kind: 'rule', name: rule.name })}
                          disabled={busy}
                          data-testid={`remove-rule-${rule.name}`}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <Dialog
          open={editing !== null}
          title={editing?.name ? `Edit ${editing.name}` : 'Add a rule'}
          onClose={() => setEditing(null)}
          data-testid="rule-dialog"
        >
          <form
            data-testid="rule-form"
            onSubmit={(event) => {
              event.preventDefault()
              void addRule()
            }}
          >
            <div className="field">
              <label htmlFor="rule-name">Rule name</label>
              <input
                id="rule-name"
                value={ruleName}
                onChange={(event) => setRuleName(event.target.value)}
                placeholder="wire transfer instructions"
                data-testid="rule-name"
                required
              />
              {/* Still said, because the name is what decides it. Renaming a rule in this dialog
                  writes a second rule rather than moving the one that was opened. */}
              <div className="hint">Saving a rule whose name already exists replaces it.</div>
            </div>

            <div className="field">
              <label htmlFor="rule-terms">Terms, one per line</label>
              <textarea
                id="rule-terms"
                style={{ minHeight: '8rem' }}
                value={terms}
                onChange={(event) => setTerms(event.target.value)}
                placeholder={'wire transfer\nrouting number\nbeneficiary account'}
                data-testid="rule-terms"
              />
              <div className="hint">
                Prefer phrases of two or three words. Matching is a plain substring test, so a lone word
                fires on ordinary conversation.
              </div>
            </div>

            <div className="field">
              <label htmlFor="rule-severity">Severity</label>
              <select
                id="rule-severity"
                value={severity}
                onChange={(event) => setSeverity(event.target.value)}
                data-testid="rule-severity"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>

            <div className="dialog-actions">
              <button type="button" onClick={() => setEditing(null)} disabled={busy}>
                Cancel
              </button>
              <button type="submit" className="primary" disabled={busy} data-testid="add-rule">
                {editing?.name ? 'Save rule' : 'Add rule'}
              </button>
            </div>
          </form>
        </Dialog>


        <div>
          <div className="card">
            <div className="card-body">
              <h3 style={{ marginTop: 0 }}>Try it on a prompt</h3>
              <textarea
                aria-label="Sample prompt"
                style={{ minHeight: '8rem' }}
                value={sample}
                onChange={(event) => setSample(event.target.value)}
                data-testid="preview-sample"
              />
              <div className="field">
                <button type="button" onClick={runPreview} disabled={busy} data-testid="run-preview">
                  Show what would match
                </button>
              </div>

              {preview && (
                <div data-testid="preview-results">
                  {preview.matches.length === 0 ? (
                    <div className="banner">Nothing in this sample matched.</div>
                  ) : (
                    preview.matches.map((match, index) => (
                      <div className="finding" key={`${match.ruleName}-${index}`}>
                        <code>{match.ruleName}</code>
                        <div>
                          <div className="mono">{match.context}</div>
                          <div className="muted">
                            matched “{match.term}” · {match.severity}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>

          {(errors.length > 0 || warnings.length > 0) && (
            <div className="card">
              <div className="card-body" data-testid="dictionary-validation">
                <h3 style={{ marginTop: 0 }}>Guidance</h3>
                {errors.map((message, index) => (
                  <div className="finding" key={`error-${index}`}>
                    <code>error</code>
                    <div>
                      <div>{message.message}</div>
                      {message.suggestion && <div className="muted">{message.suggestion}</div>}
                    </div>
                  </div>
                ))}
                {warnings.map((message, index) => (
                  <div className="finding" key={`warning-${index}`}>
                    <code>advice</code>
                    <div>
                      <div>{message.message}</div>
                      {message.suggestion && <div className="muted">{message.suggestion}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={asking?.kind === 'retire'}
        title="Retire this dictionary"
        confirmLabel={asking?.kind === 'retire' && asking.force ? 'Retire it anyway' : 'Retire it'}
        danger={asking?.kind === 'retire' && asking.force}
        busy={busy}
        onConfirm={() => {
          if (asking?.kind !== 'retire') return
          const force = asking.force
          setAsking(null)
          retire(force)
        }}
        onClose={() => setAsking(null)}
        data-testid="retire-dictionary-confirm"
      >
        {asking?.kind === 'retire' && asking.force && blocking && blocking.length > 0 ? (
          <div className="banner warning" role="note">
            {blockedNames} still {blocking.length === 1 ? 'binds' : 'bind'} this dictionary. Publishing{' '}
            {blocking.length === 1 ? 'that policy' : 'those policies'} is refused outright while the
            binding points at a retired dictionary, and the person who hits that is usually not the
            person retiring it now.
          </div>
        ) : null}

        <p>
          {detail.name} leaves the dictionary list and can no longer be edited, published or newly
          bound. What the fleet is already running does not change: a policy published with these
          terms keeps matching on them until it is republished without them.
        </p>
      </ConfirmDialog>

      <ConfirmDialog
        open={asking?.kind === 'rule'}
        title={asking?.kind === 'rule' ? `Remove '${asking.name}'` : 'Remove the rule'}
        confirmLabel="Remove it"
        danger
        busy={busy}
        onConfirm={() => {
          if (asking?.kind !== 'rule') return
          const name = asking.name
          setAsking(null)
          removeRule(name)
        }}
        onClose={() => setAsking(null)}
        data-testid="remove-rule-confirm"
      >
        <p>
          Its terms are taken out of the draft revision and there is no undo on this screen — putting
          them back means retyping every one of them. The published revision, and whatever the fleet
          is matching on today, are untouched until this draft is published.
        </p>
      </ConfirmDialog>
    </>
  )
}

function severityRank(severity: string): number {
  switch (severity) {
    case 'low':
      return 2
    case 'medium':
      return 3
    case 'high':
      return 4
    case 'critical':
      return 5
    default:
      return 1
  }
}
